import React, { useState, useEffect } from 'react'
import { T, Sheet, Spinner, probColor, fmtOdds } from './ui'
import { getNFLParlay } from './api'
import { playText } from './plain'

// Shared correlation-aware SGP slip. Legs can mix markets (a QB's pass TDs, his
// WR's rec yards, an anytime TD) — the copula prices the correlation a naive
// multiply ignores. Legs are {gsis_id, prop, market, name, team, opp, pos}.

export const legKey = l => `${l.gsis_id}:${l.prop}`

function StatCard({ label, prob, odds, hi }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', borderRadius: 10, padding: '9px 10px',
      background: hi ? T.greenDim : T.card2, border: `1px solid ${hi ? T.green : T.border}` }}>
      <div style={{ fontSize: 10, color: hi ? T.green : T.muted, letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontFamily: T.mono, fontWeight: hi ? 900 : 800, fontSize: 17, color: hi ? probColor((prob || 0) / 100) : T.muted2 }}>{prob}%</div>
      <div style={{ fontSize: 10, color: hi ? T.green : T.faint, fontFamily: T.mono }}>{fmtOdds(odds)}</div>
    </div>
  )
}

export function ParlaySheet({ legs, season, week, onClose, onRemove, onClear }) {
  const [d, setD] = useState(null)
  const [book, setBook] = useState('')
  const [copied, setCopied] = useState(false)
  const tokens = legs.map(legKey)
  // A real American price is ±100 or more. Anything smaller (a stray "10", a mistyped
  // stake) is NOT valid odds — send 0 so the backend computes no edge, and the UI warns
  // instead of pricing off a garbage number.
  const bookN = parseInt(book, 10)
  const bookValid = !isNaN(bookN) && Math.abs(bookN) >= 100
  const bookArg = bookValid ? bookN : 0

  useEffect(() => {
    if (legs.length < 2) { setD(null); return }
    let alive = true; setD(null)
    getNFLParlay(tokens, season, week, bookArg).then(r => { if (alive) setD(r) })
    return () => { alive = false }
  }, [tokens.join(','), bookArg, season, week])

  const share = () => {
    const L = (d && d.legs) || legs
    const lines = L.map(l => `• ${l.name} ${playText(l.market, l.line)}`)
    const head = d && d.corr_prob != null
      ? `LINEBRKR SGP — ${d.n} legs\nTrue ${d.corr_prob}% (${fmtOdds(d.corr_odds)}) · naive ${d.naive_prob}%`
      : `LINEBRKR SGP — ${legs.length} legs`
    const txt = `${head}\n${lines.join('\n')}`
    try { navigator.clipboard.writeText(txt).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600) }) }
    catch (_) {}
  }

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '10px 16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 900 }}>Parlay slip <span style={{ color: T.muted, fontWeight: 700, fontSize: 13 }}>· {legs.length} leg{legs.length !== 1 ? 's' : ''}</span></span>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={share} style={{ fontSize: 12, fontWeight: 700, color: T.green, background: 'transparent', border: 'none', cursor: 'pointer' }}>{copied ? '✓ copied' : 'copy'}</button>
            <button onClick={onClear} style={{ fontSize: 12, fontWeight: 700, color: T.muted, background: 'transparent', border: 'none', cursor: 'pointer' }}>clear</button>
          </div>
        </div>

        {/* legs — market-qualified */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {(d && d.legs ? d.legs : legs).map(l => (
            <div key={legKey(l)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 11px' }}>
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{l.name}</span>
                <span style={{ fontSize: 11, color: T.muted, marginLeft: 6 }}>
                  {playText(l.market, l.line)}{l.pos ? ` · ${l.pos}` : ''}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {l.prob != null && <span style={{ fontFamily: T.mono, fontWeight: 700, fontSize: 12, color: probColor(l.prob) }}>{Math.round(l.prob * 100)}%</span>}
                <span onClick={() => onRemove(l)} style={{ fontSize: 13, color: T.muted, cursor: 'pointer', fontWeight: 800 }}>✕</span>
              </div>
            </div>
          ))}
        </div>

        {legs.length < 2 ? (
          <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>
            {legs.length === 0
              ? <>Build a correlation-aware parlay: tap the <b style={{ color: T.green }}>＋</b> on any play in <b style={{ color: T.text }}>Touchdowns → Pay Dirt</b> or on a player card to add a leg. Mix any markets — we price the correlation the books ignore.</>
              : <>Add another leg — from any market — to see the correlation-adjusted price.</>}
          </div>
        ) : !d ? <Spinner /> : d.error ? (
          <div style={{ fontSize: 12.5, color: T.amber }}>{d.error}</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 9 }}>
              <StatCard label="NAIVE (multiply)" prob={d.naive_prob} odds={d.naive_odds} />
              <StatCard label="TRUE (correlated)" prob={d.corr_prob} odds={d.corr_odds} hi />
            </div>
            {Math.abs(d.correlation) >= 0.1 && (
              <div style={{ fontSize: 12, color: d.correlation >= 0 ? T.green : T.amber, marginBottom: 9 }}>
                Correlation {d.correlation >= 0 ? 'lifts' : 'lowers'} the true chance by {Math.abs(d.correlation).toFixed(1)}pp vs a naive multiply.
              </div>
            )}
            {d.pairs && d.pairs.map((pr, i) => (
              <div key={i} style={{ fontSize: 11.5, color: T.muted, marginBottom: 3 }}>
                <span style={{ color: pr.rho >= 0 ? T.green : T.amber, fontWeight: 700 }}>{pr.rho >= 0 ? '▲' : '▼'}</span>{' '}
                {pr.a.split(' ').slice(-1)[0]} + {pr.b.split(' ').slice(-1)[0]} <span style={{ color: T.faint }}>· {pr.kind}</span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <input value={book} onChange={e => {
                  // digits + sign only; auto-prefix "+" on a bare positive so a typed 10 reads as +10, not 10.
                  let v = e.target.value.replace(/[^-0-9]/g, '')
                  if (v && v[0] !== '-') v = '+' + v
                  setBook(v)
                }} inputMode="numeric"
                placeholder={`book parlay odds (fair: ${fmtOdds(d.corr_odds)})`} style={{ flex: 1, minWidth: 0, background: T.card2,
                  border: `1px solid ${bookValid ? T.green : T.border}`, borderRadius: 8, padding: '9px 11px', color: T.text, fontSize: 12.5 }} />
              {bookValid && d.edge_pct != null && (
                <span style={{ fontSize: 13, fontWeight: 800, fontFamily: T.mono, color: d.edge_pct >= 0 ? T.green : T.red }}>
                  {d.edge_pct >= 0 ? '+' : ''}{d.edge_pct}% edge</span>
              )}
            </div>
            {/* Fair-price hint + input guard — the fair number is what the ticket SHOULD pay; beat it for +EV. */}
            <div style={{ fontSize: 10.5, marginTop: 6, lineHeight: 1.45 }}>
              {book === ''
                ? <span style={{ color: T.faint }}>Fair price is <b style={{ color: T.green }}>{fmtOdds(d.corr_odds)}</b> — enter your book's parlay payout; beat fair and the edge turns green.</span>
                : bookValid
                  ? <span style={{ color: T.faint }}>Edge = our true chance − the book's implied chance at <b style={{ color: T.text }}>{fmtOdds(bookN)}</b> · fair is <b style={{ color: T.green }}>{fmtOdds(d.corr_odds)}</b>.</span>
                  : <span style={{ color: T.amber }}>Enter the real payout — American odds are ±100 or more (e.g. {fmtOdds(d.corr_odds)}).</span>}
            </div>
            <div style={{ fontSize: 10, color: T.faint, marginTop: 10 }}>
              Correlations fit from historical game logs — the co-movement a naive multiply ignores.
            </div>
          </>
        )}
      </div>
    </Sheet>
  )
}

// Floating "slip" button — shows across tabs whenever legs exist.
// Floating slip button — always visible so the parlay feature is discoverable. Empty
// (0 legs) it invites building; with legs it shows the count.
export function SlipButton({ count, onClick }) {
  const empty = !count
  return (
    <button onClick={onClick} style={{ position: 'fixed', right: 16, bottom: 74, zIndex: 150,
      maxWidth: 460, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 22,
      background: empty ? T.card : T.green, color: empty ? T.green : '#04140D',
      border: empty ? `1px solid ${T.green}` : 'none', fontWeight: 900, fontSize: 12, cursor: 'pointer',
      boxShadow: '0 6px 20px rgba(0,0,0,0.45)' }}>
      {empty ? '🎟 Parlay'
        : <>🎟 Parlay <span style={{ background: '#04140D', color: T.green, borderRadius: 12, padding: '1px 8px', fontSize: 12 }}>{count}</span></>}
    </button>
  )
}
